/**
 * MapColorsTab — Admin > Units > Map colours: the colors an operator
 * chooses for the maps' units, clients, prospects, the "place a client"
 * pin, and the resource directory map's provider organizations.
 *
 * One swatch picker per active unit (ColorSwatchPicker: the eight curated
 * swatches + a free hex field), plus the same picker for the tenant's
 * five map colors (clients, prospects, the placement pin, providers, people). A live
 * preview strip renders every chosen color as a dot on a sample of the
 * paper base (src/lib/mapColors.ts MUTED_BASE_GREY) so the whole set can
 * be judged together, the way it will actually sit on the map.
 *
 * Save writes through TWO routes at once (Promise.all): PUT
 * /admin/map/colors for the tenant's four, and one PUT
 * /admin/units/{id}/color per unit whose color actually changed — never
 * a unit that wasn't touched, so an unrelated in-flight edit from another
 * tab can't be clobbered by a blanket re-save of every unit.
 *
 * Every chosen color is checked here (contrastWarns against the paper
 * base; closeColorPairs across the WHOLE set — every unit plus the
 * four) and shown as a warning, never a block: Champagne Gold is one of
 * the eight curated swatches and the brand guide itself says "never for
 * body text," so a marginal-contrast pick is expected, not an error.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { ColorSwatchPicker } from '@/components/ColorSwatchPicker';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { useMapColors, useSetMapColors, useSetUnitColor, type UnitMapColorWire } from '@/lib/capacityApi';
import { unitDotColor } from '@/components/workspace/workspaceFormat';
import { MUTED_BASE_GREY, closeColorPairs, contrastWarns, type NamedColor } from '@/lib/mapColors';
import { contactSentence, saveFailureText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

export interface MapColorsTabProps {
  tenantSlug: string;
}

type TenantColorKey = 'clients' | 'prospects' | 'placement_pin' | 'providers' | 'people';

const TENANT_COLOR_LABELS: { key: TenantColorKey; label: string }[] = [
  { key: 'clients', label: 'Clients' },
  { key: 'prospects', label: 'Prospects' },
  { key: 'placement_pin', label: 'Placing a client (the pin)' },
  { key: 'providers', label: 'Providers (the directory map)' },
  { key: 'people', label: 'People (the directory map)' },
];

/** Every row GET /map/colors returns is a real active unit (a truthy
 *  unit_id), so unitDotColor's null branch — reached only for a MISSING
 *  unit id — never fires here; the assertion is unitDotColor's own
 *  contract, not a guess. */
function resolvedUnitColor(u: Pick<UnitMapColorWire, 'unit_id' | 'color'>): string {
  return unitDotColor(u.unit_id, u.color)!;
}

export function MapColorsTab({ tenantSlug }: MapColorsTabProps): ReactNode {
  const colors = useMapColors(tenantSlug);
  const setMapColors = useSetMapColors(tenantSlug);
  const setUnitColor = useSetUnitColor(tenantSlug);
  const contact = useSupportContact();

  // Local state holds only the operator's edits. What each picker shows is
  // derived during render: the edit when there is one, else the loaded
  // color (for a unit, the raw units.color when set, else the same
  // deterministic fallback the map itself draws, unitDotColor). So the
  // first render after the load already shows every color, and a Save's
  // own refetch never overwrites an edit in progress.
  const [unitEdits, setUnitEdits] = useState<Record<string, string>>({});
  const [tenantEdits, setTenantEdits] = useState<Partial<Record<TenantColorKey, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const unitColorOf = (u: UnitMapColorWire): string => unitEdits[u.unit_id] ?? resolvedUnitColor(u);
  const tenantColorOf = (key: TenantColorKey): string => tenantEdits[key] ?? colors[key];

  const everyChosenColor: NamedColor[] = useMemo(() => {
    const named: NamedColor[] = colors.units.map((u) => ({ key: u.unit_name, hex: unitEdits[u.unit_id] ?? resolvedUnitColor(u) }));
    for (const { key, label } of TENANT_COLOR_LABELS) named.push({ key: label, hex: tenantEdits[key] ?? colors[key] });
    return named;
  }, [colors, unitEdits, tenantEdits]);

  const closePairs = useMemo(() => closeColorPairs(everyChosenColor), [everyChosenColor]);
  const warningFor = (key: string): string | null => {
    const hex = everyChosenColor.find((c) => c.key === key)?.hex;
    const contrastWarning = hex && contrastWarns(hex) ? 'Hard to make out against the map’s paper base.' : null;
    const closePair = closePairs.find((p) => p.a === key || p.b === key);
    const closeWarning = closePair ? `Close to ${closePair.a === key ? closePair.b : closePair.a}, may be hard to tell apart.` : null;
    return [contrastWarning, closeWarning].filter(Boolean).join(' ') || null;
  };

  const dirty =
    colors.units.some((u) => unitEdits[u.unit_id] !== undefined && unitEdits[u.unit_id] !== resolvedUnitColor(u)) ||
    TENANT_COLOR_LABELS.some(({ key }) => tenantEdits[key] !== undefined && tenantEdits[key] !== colors[key]);

  const saving = setMapColors.isPending || setUnitColor.isPending;

  async function save(): Promise<void> {
    setSaveError(null);
    try {
      await setMapColors.mutateAsync({
        clients: tenantColorOf('clients'),
        prospects: tenantColorOf('prospects'),
        placement_pin: tenantColorOf('placement_pin'),
        providers: tenantColorOf('providers'),
        people: tenantColorOf('people'),
      });
      const changedUnits = colors.units.filter(
        (u) => unitEdits[u.unit_id] !== undefined && unitEdits[u.unit_id] !== resolvedUnitColor(u),
      );
      await Promise.all(changedUnits.map((u) => setUnitColor.mutateAsync({ unitId: u.unit_id, color: unitEdits[u.unit_id]! })));
    } catch (err) {
      // Two steps: the practice colors may have landed before the unit
      // colors failed, so the copy does not say nothing was changed.
      setSaveError(
        saveFailureText(err, contact, { workSafe: false }) ??
          contactSentence("The map colors didn't save. Try again, and if it happens again, tell {contact_system}.", contact),
      );
    }
  }

  return (
    <div>
      <p className="mb-4 text-sm text-ink-soft">
        Each unit's color fills its care managers' pins on the map; the four below color clients, prospects, the
        "place a client" pin, and the provider organizations on the directory map. A warning shows when a color would be hard to make out against the map's paper base, or too
        close to another color already chosen. It never blocks Save.
      </p>

      <AsyncBoundary what="the map colors" isLoading={colors.isLoading} isError={colors.isError} error={colors.error} onRetry={colors.refetch} emptyHeadline="Set up a unit and you'll see it here.">
        <div className="flex flex-col gap-5">
          <div className="rounded-card border border-sage-800/[8%] bg-paper p-4">
            <h3 className="mb-3 font-display text-sm font-semibold text-sage-800">Units</h3>
            <div className="flex flex-col gap-4">
              {colors.units.map((u) => (
                <div key={u.unit_id} data-testid={`map-colors-unit-${u.unit_id}`}>
                  <ColorSwatchPicker
                    label={u.unit_name}
                    value={unitColorOf(u)}
                    onChange={(hex) => setUnitEdits((prev) => ({ ...prev, [u.unit_id]: hex }))}
                    warning={warningFor(u.unit_name)}
                    disabled={saving}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-card border border-sage-800/[8%] bg-paper p-4">
            <h3 className="mb-3 font-display text-sm font-semibold text-sage-800">Clients, prospects, the pin, providers and people</h3>
            <div className="flex flex-col gap-4">
              {TENANT_COLOR_LABELS.map(({ key, label }) => (
                <ColorSwatchPicker
                  key={key}
                  label={label}
                  value={tenantColorOf(key)}
                  onChange={(hex) => setTenantEdits((prev) => ({ ...prev, [key]: hex }))}
                  warning={warningFor(label)}
                  disabled={saving}
                />
              ))}
            </div>
          </div>

          <div className="rounded-card border border-sage-800/[8%] p-4" style={{ backgroundColor: MUTED_BASE_GREY }}>
            <p className="mb-2 text-xs font-semibold text-ink">Preview, on the map's paper base</p>
            <div className="flex flex-wrap gap-2" data-testid="map-colors-preview">
              {everyChosenColor.map((c) => (
                <span
                  key={c.key}
                  title={c.key}
                  data-testid={`map-colors-preview-dot-${c.key}`}
                  className="inline-block h-4 w-4 rounded-full border-2 border-white shadow-sh1"
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="primary" size="sm" disabled={!dirty || saving} loading={saving} onClick={() => void save()} data-testid="map-colors-save">
              Save
            </Button>
            {saveError && (
              <p role="alert" className="text-sm text-danger" data-testid="map-colors-save-error">
                {saveError}
              </p>
            )}
          </div>
        </div>
      </AsyncBoundary>
    </div>
  );
}
